<template>
  <el-dialog
    v-model="dialogVisible"
    width="800px"
    modal-class="reconcile-dialog"
    :show-close="false"
    destroy-on-close
  >
    <template #header>
      <div class="reconcile-header">
        <div class="header-left">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16.2513 12.4835C16.2346 12.4835 16.2263 12.4835 16.2096 12.4918C16.0013 11.0835 14.8013 10.0002 13.3346 10.0002C12.168 10.0002 11.168 10.6918 10.7013 11.6835C9.38464 11.7502 8.33464 12.8335 8.33464 14.1668C8.33464 15.5502 9.4513 16.6668 10.8346 16.6668L16.2513 16.6502C17.4013 16.6502 18.3346 15.7168 18.3346 14.5668C18.3346 13.4168 17.4013 12.4835 16.2513 12.4835ZM16.2596 15.0002H10.8346C10.3763 15.0002 10.0013 14.6252 10.0013 14.1668C10.0013 13.7085 10.3763 13.3335 10.8346 13.3335H11.8763V13.1252C11.8763 12.3168 12.5263 11.6668 13.3346 11.6668C14.143 11.6668 14.793 12.3168 14.793 13.1252V14.1668C14.793 14.1668 16.2513 14.1668 16.2596 14.1668C16.493 14.1668 16.6763 14.3502 16.6763 14.5835C16.668 14.8085 16.4846 15.0002 16.2596 15.0002ZM6.66797 3.55016V5.29183C4.7263 5.9835 3.33464 7.82516 3.33464 10.0002C3.33464 11.4752 3.98464 12.7835 5.0013 13.7002V11.6668H6.66797V16.6668H1.66797V15.0002H3.94297C2.5513 13.7835 1.66797 12.0002 1.66797 10.0002C1.66797 6.89183 3.79297 4.29183 6.66797 3.55016ZM15.0013 5.00016H12.7263C13.918 6.05016 14.7346 7.5085 14.943 9.16683H13.2596C13.068 8.0335 12.4846 7.04183 11.668 6.30016V8.3335H10.0013V3.3335H15.0013V5.00016Z" fill="white"/>
          </svg>
          <span>Sync Patient's History from Surescripts</span>
        </div>
        <button class="close-btn" @click="closeDialog">
          <img src="@/assets/MyPatient/close.svg" alt="Close" />
        </button>
      </div>
    </template>

    <div class="reconcile-content">
      <div class="reconcile-section">
        <div class="section-header" :class="{ 'expanded': medicationsExpanded }" @click="medicationsExpanded = !medicationsExpanded">
          <div class="section-title">
            <svg class="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M10.5 15H8V12H10.5V9.5H13.5V12H16V15H13.5V17.5H10.5V15ZM19 8V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V8C5 6.9 5.9 6 7 6H17C18.1 6 19 6.9 19 8ZM17 8H7V19H17V8ZM18 3H6V5H18V3Z" fill="#374957"/>
            </svg>
            <span>Medication History ({{ newMedicationsCount }} new)</span>
          </div>
          <div class="header-actions">
            <div class="select-all-btn" @click.stop>
              <Checkbox
                :status="allNewMedicationsSelected ? 'checked' : 'uncheck'"
                @changeStatus="toggleAllNewMedications"
              />
              <span>Select All New</span>
            </div>
            <svg class="expand-icon" :class="{ expanded: medicationsExpanded }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="#374957" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div v-show="medicationsExpanded" class="section-content">
          <div
            v-for="med in localNewMedications"
            :key="med.id"
            class="reconcile-item"
            :class="{ 'clickable': med.isNew }"
            @click="med.isNew && toggleNewMedicationClick(med.id)"
          >
            <div class="item-row">
              <Checkbox
                v-if="med.isNew"
                :status="selectedNewMedications.has(med.id) ? 'checked' : 'uncheck'"
                @changeStatus="toggleNewMedication(med.id, $event)"
                @click.stop
              />
              <div v-else class="checkbox-placeholder"></div>
              <span class="item-name">{{ med.name }} {{ med.dose }}</span>
              <span class="status-tag" :class="{ 'new': med.isNew }">
                {{ med.isNew ? 'Newly Added' : 'Reconciled' }}
              </span>
            </div>
            <div class="item-row sub-row">
              <span class="item-details">{{ formatFrequency(med) }} <span class="dot">•</span> <span class="active-tag" :class="{ 'inactive': !med.isActive }">{{ med.isActive ? 'Active' : 'Inactive' }}</span></span>
            </div>
          </div>
          <div v-if="localNewMedications.length === 0" class="empty-state">
            No new medications
          </div>
        </div>
      </div>

      <div class="reconcile-section">
        <div class="section-header" :class="{ 'expanded': allergiesExpanded }" @click="allergiesExpanded = !allergiesExpanded">
          <div class="section-title">
            <svg class="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19.5 6C18.19 6 17.13 7.01 17.02 8.3C15.14 7.8 14.18 6.5 12 6.5C9.81 6.5 8.86 7.8 6.98 8.3C6.87 7.02 5.81 6 4.5 6C3.12 6 2 7.12 2 8.5V9C2 15 5.6 16.81 8.52 16.98C9.53 17.62 10.72 18 12 18C13.28 18 14.47 17.62 15.48 16.98C18.4 16.81 22 15 22 9V8.5C22 7.12 20.88 6 19.5 6ZM3.5 9V8.5C3.5 7.95 3.95 7.5 4.5 7.5C5.05 7.5 5.5 7.95 5.5 8.5V11.5C5.5 12.78 5.88 13.97 6.51 14.98C4.99 14.27 3.5 12.65 3.5 9ZM7 11.5V9.85C8.12 9.62 8.95 9.16 9.66 8.77C10.48 8.33 11.07 8 12 8C12.93 8 13.52 8.33 14.34 8.78C15.05 9.17 15.88 9.62 17 9.86V11.51C17 14.27 14.76 16.51 12 16.51C9.24 16.51 7 14.26 7 11.5ZM20.5 9C20.5 12.65 19.01 14.27 17.49 14.98C18.13 13.97 18.5 12.78 18.5 11.5V8.5C18.5 7.95 18.95 7.5 19.5 7.5C20.05 7.5 20.5 7.95 20.5 8.5V9ZM10.69 10.48C10.25 10.74 9.73 11.04 9 11.24V10.2C9.48 10.03 9.84 9.82 10.18 9.62C10.72 9.3 11.23 9 12 9C12.77 9 13.27 9.3 13.8 9.62C14.14 9.82 14.51 10.04 15 10.21V11.25C14.25 11.04 13.74 10.74 13.29 10.47C12.83 10.2 12.49 10 12 10C11.51 10 11.16 10.2 10.69 10.48Z" fill="#374957"/>
            </svg>
            <span>Allergy History ({{ newAllergiesCount }} new)</span>
          </div>
          <div class="header-actions">
            <div class="select-all-btn" @click.stop>
              <Checkbox
                :status="allNewAllergiesSelected ? 'checked' : 'uncheck'"
                @changeStatus="toggleAllNewAllergies"
              />
              <span>Select All New</span>
            </div>
            <svg class="expand-icon" :class="{ expanded: allergiesExpanded }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="#374957" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
        <div v-show="allergiesExpanded" class="section-content">
          <div
            v-for="allergy in localNewAllergies"
            :key="allergy.id"
            class="reconcile-item"
            :class="{ 'clickable': allergy.isNew }"
            @click="allergy.isNew && toggleNewAllergyClick(allergy.id)"
          >
            <div class="item-row">
              <Checkbox
                v-if="allergy.isNew"
                :status="selectedNewAllergies.has(allergy.id) ? 'checked' : 'uncheck'"
                @changeStatus="toggleNewAllergy(allergy.id, $event)"
                @click.stop
              />
              <div v-else class="checkbox-placeholder"></div>
              <span class="item-name">{{ allergy.allergen }}</span>
              <span class="status-tag" :class="{ 'new': allergy.isNew }">
                {{ allergy.isNew ? 'Newly Added' : 'Reconciled' }}
              </span>
            </div>
            <div class="item-row sub-row">
              <span class="item-details">{{ allergy.reactions }} <span class="dot">•</span> <span class="severity-tag" :class="allergy.severity">
                {{ formatSeverity(allergy.severity) }}
              </span></span>
            </div>
          </div>
          <div v-if="localNewAllergies.length === 0" class="empty-state">
            No new allergies
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="reconcile-footer">
        <button class="cancel-btn" @click="closeDialog">Cancel</button>
        <button class="add-btn" :disabled="totalSelected === 0" @click="handleAddSelected">
          Sync Selected
        </button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import Checkbox from '@/NewLibrary/Checkbox.vue';

export default {
  name: 'ReconcileDialog',
  components: {
    Checkbox
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    newMedications: {
      type: Array,
      default: () => []
    },
    newAllergies: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'add-selected'],
  data() {
    return {
      medicationsExpanded: true,
      allergiesExpanded: true,
      selectedNewMedications: new Set(),
      selectedNewAllergies: new Set(),
      localNewMedications: [],
      localNewAllergies: []
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetSelections();
      }
    },
    newMedications: {
      immediate: true,
      handler(newVal) {
        this.localNewMedications = [...(newVal || [])];
      }
    },
    newAllergies: {
      immediate: true,
      handler(newVal) {
        this.localNewAllergies = [...(newVal || [])];
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    newMedicationsCount() {
      return this.localNewMedications.filter(med => med.isNew).length;
    },
    newAllergiesCount() {
      return this.localNewAllergies.filter(allergy => allergy.isNew).length;
    },
    allNewMedicationsSelected() {
      const newMeds = this.localNewMedications.filter(med => med.isNew);
      return newMeds.length > 0 &&
        newMeds.every(med => this.selectedNewMedications.has(med.id));
    },
    allNewAllergiesSelected() {
      const newAllergies = this.localNewAllergies.filter(allergy => allergy.isNew);
      return newAllergies.length > 0 &&
        newAllergies.every(allergy => this.selectedNewAllergies.has(allergy.id));
    },
    totalSelected() {
      return this.selectedNewMedications.size +
        this.selectedNewAllergies.size;
    }
  },
  methods: {
    formatSeverity(severity) {
      const severityMap = {
        'mild': 'Mild',
        'moderate': 'Moderate',
        'severe': 'Severe'
      };
      return severityMap[severity] || severity;
    },

    formatFrequency(med) {
      return `${med.frequencyNumber} ${med.frequencyPeriod}`;
    },

    toggleNewMedication(id, status) {
      if (status === 'checked') {
        this.selectedNewMedications.add(id);
      } else {
        this.selectedNewMedications.delete(id);
      }
      this.selectedNewMedications = new Set(this.selectedNewMedications);
    },

    toggleNewMedicationClick(id) {
      if (this.selectedNewMedications.has(id)) {
        this.selectedNewMedications.delete(id);
      } else {
        this.selectedNewMedications.add(id);
      }
      this.selectedNewMedications = new Set(this.selectedNewMedications);
    },
    toggleAllNewMedications(status) {
      if (status === 'checked') {
        this.localNewMedications.forEach(med => {
          if (med.isNew) {
            this.selectedNewMedications.add(med.id);
          }
        });
      } else {
        this.selectedNewMedications.clear();
      }
      this.selectedNewMedications = new Set(this.selectedNewMedications);
    },

    toggleNewAllergy(id, status) {
      if (status === 'checked') {
        this.selectedNewAllergies.add(id);
      } else {
        this.selectedNewAllergies.delete(id);
      }
      this.selectedNewAllergies = new Set(this.selectedNewAllergies);
    },

    toggleNewAllergyClick(id) {
      if (this.selectedNewAllergies.has(id)) {
        this.selectedNewAllergies.delete(id);
      } else {
        this.selectedNewAllergies.add(id);
      }
      this.selectedNewAllergies = new Set(this.selectedNewAllergies);
    },
    toggleAllNewAllergies(status) {
      if (status === 'checked') {
        this.localNewAllergies.forEach(allergy => {
          if (allergy.isNew) {
            this.selectedNewAllergies.add(allergy.id);
          }
        });
      } else {
        this.selectedNewAllergies.clear();
      }
      this.selectedNewAllergies = new Set(this.selectedNewAllergies);
    },

    resetSelections() {
      this.selectedNewMedications = new Set();
      this.selectedNewAllergies = new Set();
    },

    closeDialog() {
      this.$emit('close');
    },

    handleAddSelected() {
      const selectedItems = {
        newMedications: this.localNewMedications.filter(m =>
          this.selectedNewMedications.has(m.id)
        ),
        newAllergies: this.localNewAllergies.filter(a =>
          this.selectedNewAllergies.has(a.id)
        )
      };
      this.$emit('add-selected', selectedItems);
    }
  }
};
</script>

<style>
.reconcile-dialog .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.reconcile-dialog .el-dialog {
  border-radius: 10px;
  margin: 0 auto;
  background-color: #F9FBFD;
  --el-dialog-padding-primary: 0px !important;
}

.reconcile-dialog .el-dialog__header {
  padding: 0;
  margin: 0;
}

.reconcile-dialog .el-dialog__body {
  padding: 0px 0px;
  margin: 0;
}

.reconcile-dialog .el-dialog__footer {
  padding: 0;
  margin: 0;
}
</style>

<style scoped>
.reconcile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: #004879;
  color: white;
  border-radius: 10px 10px 0 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reconcile-content {
  padding: 16px 24px;
}

.reconcile-section {
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--Neutral-White, #FFF);
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0px;
  cursor: pointer;
}

.section-header.expanded {
  border-bottom: 1px dashed var(--DividerGray, #C4C4C4);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.15px;
}

.section-icon {
  flex-shrink: 0;
}

.expand-icon {
  transition: transform 0.2s;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.select-all-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  cursor: pointer;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.section-content {
  padding: 8px 8px 8px 0;
  max-height: 180px;
  overflow-y: auto;
}

.reconcile-item {
  display: flex;
  width: 100%;
  padding: 4px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
}

.reconcile-item.clickable {
  cursor: pointer;
}

.checkbox-placeholder {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.item-name {
  flex: 1;
  overflow: hidden;
  color: var(--TrueBlack, #333);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.item-details {
  color: #666;
  font-size: 12px;
}

.sub-row {
  padding-left: 32px;
}

.dot {
  margin: 0 4px;
  color: #999;
}

.status-tag {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  background: #EBFFF8;
  color: #25B8C2;
}

.status-tag.new {
  background: #FEF9E9;
  color: #F06539;
}

.active-tag {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  background: #E5F9EF;
  color: #25B8C2;
}

.active-tag.inactive {
  background: #F5F5F5;
  color: #666;
}

.severity-tag {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.severity-tag.severe {
  background: #F6CCCC;
  color: #D10000;
}

.severity-tag.moderate {
  background: #F7EECE;
  color: #F06539;
}

.severity-tag.mild {
  background: #E3F8F9;
  color: #25B8C2;
}

.empty-state {
  padding: 16px;
  text-align: center;
  color: #999;
  font-style: italic;
  font-size: 13px;
}

.reconcile-footer {
  display: flex;
  justify-content: space-between;
  padding: 16px 24px 24px;
}

.cancel-btn {
  padding: 8px 24px;
  background-color: white;
  color: #D10000;
  border: 1px solid #D10000;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background-color: #FFF0F0;
}

.add-btn {
  padding: 8px 24px;
  background-color: #004879;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.add-btn:hover {
  background-color: #1E4670;
}

.add-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #999;
}

.section-content::-webkit-scrollbar {
  width: 4px;
}

.section-content::-webkit-scrollbar-track {
  background: transparent;
}

.section-content::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

.section-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>
